import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import RichTextEditor from '@/components/RichTextEditor';
import {
  DevelopmentService,
  Development,
} from '@/services/developmentService';
import {
  DevelopmentFaq,
  DevelopmentFaqService,
} from '@/services/developmentFaqService';
import {
  ArrowLeft,
  HelpCircle,
  Loader2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';

type FaqDraft = {
  id?: number;
  question: string;
  answer_html: string;
  is_active: boolean;
  saving?: boolean;
  isNew?: boolean;
};

const emptyDraft = (): FaqDraft => ({
  question: '',
  answer_html: '',
  is_active: true,
  isNew: true,
});

const AdminDevelopmentFaq = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const developmentId = Number(id);

  const [development, setDevelopment] = useState<Development | null>(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<FaqDraft[]>([]);

  const loadData = useCallback(async () => {
    if (!Number.isFinite(developmentId) || developmentId <= 0) {
      toast({
        title: 'Empreendimento inválido',
        variant: 'destructive',
      });
      navigate('/admin/empreendimentos');
      return;
    }

    try {
      setLoading(true);
      const [dev, faqs] = await Promise.all([
        DevelopmentService.getDevelopmentById(developmentId),
        DevelopmentFaqService.listAdmin(developmentId),
      ]);

      if (!dev) {
        toast({
          title: 'Empreendimento não encontrado',
          variant: 'destructive',
        });
        navigate('/admin/empreendimentos');
        return;
      }

      setDevelopment(dev);
      setItems(
        faqs.map((f) => ({
          id: f.id,
          question: f.question,
          answer_html: f.answer_html,
          is_active: f.is_active,
        })),
      );
    } catch (error) {
      console.error(error);
      toast({
        title: 'Erro ao carregar FAQ',
        description: 'Tente novamente em instantes.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [developmentId, navigate, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateItem = (index: number, patch: Partial<FaqDraft>) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  const handleAdd = () => {
    setItems((prev) => [...prev, emptyDraft()]);
  };

  const stripHtml = (html: string) =>
    html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

  const handleSave = async (index: number) => {
    const item = items[index];
    const question = item.question.trim();
    const answer = item.answer_html.trim();

    if (!question) {
      toast({
        title: 'Pergunta obrigatória',
        variant: 'destructive',
      });
      return;
    }
    if (!stripHtml(answer)) {
      toast({
        title: 'Resposta obrigatória',
        description: 'Informe texto, imagem ou link na resposta.',
        variant: 'destructive',
      });
      return;
    }

    updateItem(index, { saving: true });
    try {
      let saved: DevelopmentFaq;
      if (item.id && !item.isNew) {
        saved = await DevelopmentFaqService.update(developmentId, item.id, {
          question,
          answer_html: answer,
          is_active: item.is_active,
        });
      } else {
        saved = await DevelopmentFaqService.create(developmentId, {
          question,
          answer_html: answer,
          is_active: item.is_active,
        });
      }

      updateItem(index, {
        id: saved.id,
        question: saved.question,
        answer_html: saved.answer_html,
        is_active: saved.is_active,
        isNew: false,
        saving: false,
      });

      toast({ title: 'FAQ salva com sucesso' });
    } catch (error) {
      console.error(error);
      updateItem(index, { saving: false });
      toast({
        title: 'Erro ao salvar',
        description: 'Verifique os dados e tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (index: number) => {
    const item = items[index];
    if (!confirm('Excluir esta pergunta?')) return;

    if (!item.id || item.isNew) {
      setItems((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    try {
      await DevelopmentFaqService.delete(developmentId, item.id);
      setItems((prev) => prev.filter((_, i) => i !== index));
      toast({ title: 'FAQ excluída' });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Erro ao excluir',
        variant: 'destructive',
      });
    }
  };

  const uploadImage = useCallback(
    (file: File) => DevelopmentFaqService.uploadFaqImage(developmentId, file),
    [developmentId],
  );

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/empreendimentos')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <HelpCircle className="h-7 w-7 text-primary" />
                FAQ do empreendimento
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                {development?.name ?? 'Carregando...'}
              </p>
            </div>
          </div>
          <Button type="button" onClick={handleAdd} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar pergunta
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Nenhuma pergunta cadastrada. Clique em &quot;Adicionar pergunta&quot;
              para começar.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item, index) => (
              <Card key={item.id ?? `new-${index}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium">
                    Pergunta {index + 1}
                    {!item.is_active && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        (inativa)
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={`question-${index}`}>Pergunta</Label>
                    <Input
                      id={`question-${index}`}
                      value={item.question}
                      onChange={(e) =>
                        updateItem(index, { question: e.target.value })
                      }
                      placeholder="Ex.: Qual o horário do check-in?"
                      maxLength={500}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Resposta</Label>
                    <RichTextEditor
                      value={item.answer_html}
                      onChange={(value) =>
                        updateItem(index, { answer_html: value })
                      }
                      placeholder="Texto, links e imagens na resposta..."
                      minHeight="160px"
                      richContent
                      onImageUpload={uploadImage}
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`active-${index}`}
                        checked={item.is_active}
                        onCheckedChange={(checked) =>
                          updateItem(index, { is_active: checked })
                        }
                      />
                      <Label htmlFor={`active-${index}`} className="cursor-pointer">
                        Exibir no site
                      </Label>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleDelete(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleSave(index)}
                        disabled={item.saving}
                      >
                        {item.saving ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Salvar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDevelopmentFaq;
